/**
 * Location Performance — column model.
 *
 * The first column is the grouped dimension, so its LABEL is not fixed: the
 * catalog is built per run from whatever the report was grouped by. Everything
 * downstream (chooser, saved view, export) therefore takes a catalog as an
 * argument rather than importing a constant, exactly as the Conversation
 * Detailed Report does for its tenant-defined properties.
 */
import type { ReportColumnDef } from '../agent-performance/columns.js'

export const LOCATION_PERFORMANCE_REPORT_ID = 'conversation.location-performance'

/** The grouped column is pinned: a metric row with no label is unreadable. */
export function buildLocationPerformanceColumns(groupLabel: string): ReportColumnDef[] {
  return [
    { key: 'groupLabel', label: groupLabel, width: 260, stickyLeft: 0, locked: true, type: 'text' },
    { key: 'conversations', label: 'Conversations', width: 170, align: 'right', type: 'number' },
    { key: 'sharePct', label: 'Share%', width: 140, align: 'right', type: 'percent' },
    { key: 'open', label: 'Open', width: 130, align: 'right', type: 'number' },
    { key: 'resolved', label: 'Resolved', width: 140, align: 'right', type: 'number' },
    { key: 'closed', label: 'Closed', width: 140, align: 'right', type: 'number' },
    { key: 'unanswered', label: 'Awaiting First Reply', width: 200, align: 'right', type: 'number' },
    { key: 'avgResponseSeconds', label: 'Average Response Time', width: 230, align: 'right', type: 'duration' },
    { key: 'avgResolutionSeconds', label: 'Average Resolution Time', width: 240, align: 'right', type: 'duration' },
    { key: 'slaBreached', label: 'SLA Breached', width: 170, align: 'right', type: 'number' },
    { key: 'slaBreachPct', label: 'SLA Breach%', width: 170, align: 'right', type: 'percent' },
    { key: 'olaBreached', label: 'OLA Breached', width: 170, align: 'right', type: 'number' },
    { key: 'olaBreachPct', label: 'OLA Breach%', width: 170, align: 'right', type: 'percent' },
    { key: 'csatCount', label: 'CSAT Responses', width: 180, align: 'right', type: 'number' },
    { key: 'csatAvg', label: 'Average CSAT', width: 170, align: 'right', type: 'number' },
  ]
}

/**
 * Reconcile a stored view against the catalog: unknown keys are dropped,
 * locked columns forced back in, and an empty result falls back to everything.
 * A stale preference can therefore never render an unusable table.
 */
export function sanitizeLocationColumns(
  saved: unknown,
  catalog: ReportColumnDef[],
): string[] {
  const all = catalog.map((c) => c.key)
  if (!Array.isArray(saved)) return all
  const known = new Set(all)
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const key of saved) {
    if (typeof key !== 'string' || !known.has(key) || seen.has(key)) continue
    seen.add(key)
    ordered.push(key)
  }
  for (const col of catalog) {
    if (col.locked && !seen.has(col.key)) {
      seen.add(col.key)
      ordered.unshift(col.key)
    }
  }
  return ordered.length > 0 ? ordered : all
}
